/**
 * Utility functions for handling sub_title display and validation
 */

/**
 * Checks if a sub_title value is valid and should be displayed
 * Handles null, undefined, empty strings, and whitespace-only strings gracefully
 * @param subTitle - The sub_title value to validate
 * @returns true if the sub_title is valid and should be displayed
 */
export const isValidSubTitle = (subTitle: string | null | undefined): boolean => {
  return Boolean(subTitle && typeof subTitle === 'string' && subTitle.trim() !== '');
};

/**
 * Safely gets a trimmed sub_title value for display
 * Returns null if the sub_title is invalid
 * @param subTitle - The sub_title value to process
 * @returns trimmed sub_title string or null
 */
export const getDisplaySubTitle = (subTitle: string | null | undefined): string | null => {
  if (!isValidSubTitle(subTitle)) {
    return null;
  }
  return (subTitle as string).trim();
};

/**
 * React component helper for conditional sub_title rendering
 * @param subTitle - The sub_title value to check
 * @param renderFn - Function to render the sub_title if valid
 * @returns JSX element or null
 */
export const renderSubTitleIfValid = (
  subTitle: string | null | undefined,
  renderFn: (validSubTitle: string) => JSX.Element
): JSX.Element | null => {
  const displaySubTitle = getDisplaySubTitle(subTitle);
  return displaySubTitle ? renderFn(displaySubTitle) : null;
};
